import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Modal,
  Descriptions,
  Row,
  Col,
  Statistic,
  Typography,
  message,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Create axios instance with JWT authentication for merchant
const merchantApi = axios.create({
  baseURL: '/api/merchant',
  timeout: 30000,
});

// Request interceptor to add JWT token
merchantApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('merchant_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
merchantApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('merchant_access_token');
      localStorage.removeItem('merchant_refresh_token');
      localStorage.removeItem('merchant_user');
      window.location.href = '/merchant/login';
    }
    return Promise.reject(error);
  }
);

const MerchantTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [statistics, setStatistics] = useState({});

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [merchantId, setMerchantId] = useState(null);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      console.log('Stored merchant data:', storedData);
      if (storedData) {
        const parsed = JSON.parse(storedData);
        console.log('Parsed merchant data:', parsed);
        setMerchantData(parsed);

        // Use user.id for transactions route (not member_id)
        const merchantId = parsed.user_id || parsed.id;
        setMerchantId(merchantId);
        console.log('Setting merchant ID for transactions:', merchantId, 'from fields:', {
          user_id: parsed.user_id,
          id: parsed.id,
          member_id: parsed.member_id
        });
      } else {
        // For development/testing - use test merchant ID
        console.log('No merchant data found, using fallback ID 2');
        setMerchantId(2);
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      setMerchantId(2);
    }
  }, []);

  useEffect(() => {
    if (merchantId) {
      fetchTransactions();
    }
  }, [pagination.current, pagination.pageSize, filters, merchantId]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        merchant_id: merchantId
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      console.log('Fetching transactions for merchant ID:', merchantId, 'with params:', params);
      const response = await merchantApi.get('/transactions', { params });
      console.log('Transactions response:', response.data);

      setTransactions(response.data.transactions || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.totalItems || response.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      message.error('Gagal memuat data transaksi');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetail = async (transactionId) => {
    try {
      const params = {};
      if (merchantId) {
        params.merchant_id = merchantId;
      }
      const response = await merchantApi.get(`/transactions/${transactionId}`, { params });
      setSelectedTransaction(response.data.transaction || response.data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch transaction detail:', error);
      message.error('Gagal memuat detail transaksi');
    }
  };

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  const exportToCSV = async () => {
    const hide = message.loading('Mengambil semua data...', 0);
    try {
      const baseParams = {};
      if (merchantId) baseParams.merchant_id = merchantId;
      if (filters.search) baseParams.search = filters.search;
      if (filters.status) baseParams.status = filters.status;
      if (filters.date_from) baseParams.date_from = filters.date_from;
      if (filters.date_to) baseParams.date_to = filters.date_to;

      // Ambil SEMUA baris via paginasi. Sebelumnya dibatasi limit 5000 -> baris
      // ke-5001 dst tidak ikut terexport. Sekarang loop sampai seluruh data terambil.
      const pageSize = 2000;
      let allData = [];
      let page = 1;
      let total = Infinity;
      while (allData.length < total) {
        const resp = await merchantApi.get('/transactions', { params: { ...baseParams, page, limit: pageSize } });
        const batch = (resp.data && resp.data.transactions) || [];
        const t = resp.data && resp.data.pagination && resp.data.pagination.total;
        total = (t != null) ? t : (allData.length + batch.length);
        allData = allData.concat(batch);
        if (batch.length < pageSize) break; // halaman terakhir
        page++;
        if (page > 1000) break; // pengaman (maks ~2 juta baris)
      }
      hide();

      if (allData.length === 0) {
        message.warning('Tidak ada data untuk di-export');
        return;
      }

      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const headers = ['ID Transaksi', 'Order ID Mitra', 'Jumlah', 'Fee', 'Total Diterima', 'Status', 'Metode Pembayaran', 'Tanggal'];
      const rows = allData.map(t => [
        t.transaction_id || '',
        t.external_id || '',
        t.amount || 0,
        t.fee_merchant || 0,
        t.total_amount || 0,
        t.status || '',
        t.payment_method_name || '',
        dayjs(t.created_at).format('DD/MM/YYYY HH:mm'),
      ]);

      const csvContent = [
        headers.map(esc).join(','),
        ...rows.map(row => row.map(esc).join(','))
      ].join('\n');

      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `transaksi-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success(`${allData.length} transaksi berhasil di-export`);
    } catch (err) {
      hide();
      message.error('Gagal mengambil data export');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      success: 'green',
      completed: 'green', // For backward compatibility
      failed: 'red',
      expired: 'volcano',
      cancelled: 'gray',
      refunded: 'purple'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      processing: <ClockCircleOutlined />,
      success: <CheckCircleOutlined />,
      completed: <CheckCircleOutlined />, // For backward compatibility
      failed: <CloseCircleOutlined />,
      expired: <ClockCircleOutlined />,
      cancelled: <CloseCircleOutlined />,
      refunded: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const getTypeColor = (type) => {
    const colors = {
      payment: 'blue',
      topup: 'green',
      withdrawal: 'orange',
      transfer: 'purple'
    };
    return colors[type] || 'default';
  };

  const columns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          <Text copyable={{ text }} style={{ fontFamily: 'monospace' }}>
            {text.substring(0, 20)}...
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Order ID Mitra',
      dataIndex: 'external_id',
      key: 'external_id',
      width: 160,
      render: (val) => val ? <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>{val}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount) => (
        <Text strong>{formatCurrency(amount)}</Text>
      ),
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Fee',
      dataIndex: 'fee_merchant',
      key: 'fee_merchant',
      width: 100,
      align: 'right',
      render: (fee) => (
        <Text type="secondary">{formatCurrency(fee)}</Text>
      ),
    },
    {
      title: 'Total Diterima',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (total, record) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(Number(total) - Number(record.fee_merchant || 0))}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {(status || '-').toString().toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Metode Pembayaran',
      dataIndex: 'payment_method_name',
      key: 'payment_method_name',
      width: 150,
      render: (method, record) => (
        method ? (
          <Space>
            {record.payment_method_icon && (
              <img
                src={record.payment_method_icon}
                alt={method}
                style={{ width: 20, height: 20 }}
              />
            )}
            <Text>{method}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => (
        <div>
          <Text>{dayjs(date).format('DD/MM/YYYY')}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(date).format('HH:mm')}
          </Text>
        </div>
      ),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Tooltip title="Lihat Detail">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EyeOutlined />}
              onClick={() => fetchTransactionDetail(record.transaction_id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Show loading state if merchant data not ready
  if (!merchantId) {
    return (
      <div style={{ padding: '24px' }}>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Riwayat Transaksi</Title>
        <Text type="secondary">Kelola dan pantau semua transaksi Anda{merchantData ? ` (${merchantData.full_name || merchantData.merchant_name})` : ''}</Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Transaksi"
              value={pagination.total}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Berhasil"
              value={transactions.filter(t => ['success','paid','completed'].includes(t.status)).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending"
              value={transactions.filter(t => t.status === 'pending').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Gagal"
              value={transactions.filter(t => ['failed','reversed'].includes(t.status)).length}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        {/* Filters */}
        <div className="mb-4">
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Cari ID transaksi atau deskripsi"
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
              >
                <Option value="pending">Pending</Option>
                <Option value="processing">Processing</Option>
                <Option value="success">Success</Option>
                <Option value="failed">Failed</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="refunded">Refunded</Option>
              </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={(dates) => {
                  if (dates) {
                    handleFilterChange('date_from', dates[0].format('YYYY-MM-DD'));
                    handleFilterChange('date_to', dates[1].format('YYYY-MM-DD'));
                  } else {
                    handleFilterChange('date_from', '');
                    handleFilterChange('date_to', '');
                  }
                }}
              />
            </Col>
            <Col span={4}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchTransactions}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  type="primary"
                  ghost
                  onClick={exportToCSV}
                >
                  Export
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} transaksi`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Detail Transaksi"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedTransaction(null);
        }}
        footer={null}
        width={800}
      >
        {selectedTransaction && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID Transaksi" span={2}>
              <Text copyable style={{ fontFamily: 'monospace' }}>
                {selectedTransaction.transaction_id}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTransaction.status)} icon={getStatusIcon(selectedTransaction.status)}>
                {(selectedTransaction.status || '-').toString().toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Order ID Mitra">
              {selectedTransaction.external_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Jumlah">
              <Text strong style={{ fontSize: '16px' }}>
                {formatCurrency(selectedTransaction.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fee">
              <Text type="secondary">
                {formatCurrency(selectedTransaction.fee_merchant || selectedTransaction.fee || 0)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Total Diterima" span={2}>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                {formatCurrency(Number(selectedTransaction.total_amount) - Number(selectedTransaction.fee_merchant || selectedTransaction.fee || 0))}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Metode Pembayaran" span={2}>
              {selectedTransaction.payment_method_name ? (
                <Space>
                  {selectedTransaction.payment_method_icon && (
                    <img
                      src={selectedTransaction.payment_method_icon}
                      alt={selectedTransaction.payment_method_name}
                      style={{ width: 24, height: 24 }}
                    />
                  )}
                  <Text>{selectedTransaction.payment_method_name}</Text>
                  <Text type="secondary">({selectedTransaction.payment_provider_name})</Text>
                </Space>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Deskripsi" span={2}>
              {selectedTransaction.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Order ID Merchant">
              {selectedTransaction.merchant_order_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Provider Transaction ID">
              {selectedTransaction.provider_transaction_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tanggal Dibuat">
              {dayjs(selectedTransaction.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Tanggal Selesai">
              {selectedTransaction.completed_at
                ? dayjs(selectedTransaction.completed_at).format('DD/MM/YYYY HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Kadaluarsa">
              {selectedTransaction.expired_at
                ? dayjs(selectedTransaction.expired_at).format('DD/MM/YYYY HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Mata Uang">
              {selectedTransaction.currency || 'IDR'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantTransactions;